/**
 * WebSmName — before the doors, the agency asks what to call them. Their first
 * name is saved (the texts address them by it) and remembered, so the key they
 * earn after the first debrief comes already engraved with it.
 *
 * Options: Eyebrow (optional) / Title (optional) / Prompt / Hint / Placeholder / LabelForCLICK1 / BgColor
 *   / AccentColor. Emits CLICK1 once the name is saved (or the save failed —
 *   a name only personalises texts, so it never strands them here).
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  ACTION_BTN_CLASS,
  SECTION_CLASS,
  TITLE_CLASS,
  stepVars,
} from "./step-shell";
import {
  NAME_MAX,
  NAME_RE,
  cleanName,
  lastName,
  saveName,
} from "./operative-name";
import type { StepTypeProps } from "./types";

export default function WebSmNameType({ getOpt, dispatch }: StepTypeProps) {
  const accent = getOpt("AccentColor") || "#d6a94e";
  const bg = getOpt("BgColor") || "#0a0809";
  const eyebrow = getOpt("Eyebrow");
  const title = getOpt("Title");
  const prompt = getOpt("Prompt") || "What should I call you?";
  const hint =
    getOpt("Hint") || "First name, nickname, whatever your mother called you.";
  const placeholder = getOpt("Placeholder") || "Name or nickname";
  const button = getOpt("LabelForCLICK1") || "That's me";

  const [name, setName] = useState(lastName);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const trimmed = name.trim();
  const valid = trimmed.length > 0 && NAME_RE.test(trimmed);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || pending.current) return;
    pending.current = true;
    (document.activeElement as HTMLElement | null)?.blur();
    setBusy(true);
    try {
      await saveName(trimmed);
    } catch {
      if (!alive.current) return;
      toast.error("Couldn't save your name — carrying on without it.");
    }
    if (alive.current) dispatch("CLICK1");
  };

  return (
    <section
      style={stepVars({ accent, bg })}
      className={`${SECTION_CLASS} px-4`}
    >
      <form
        onSubmit={submit}
        className="relative z-[2] w-full max-w-[380px] text-center"
      >
        {eyebrow ? (
          <div className="mb-4 font-stencil text-[11px] tracking-[4px] text-[var(--sm-accent)] uppercase [text-shadow:0_0_14px_var(--sm-accent-soft)]">
            ▸ {eyebrow} ◂
          </div>
        ) : null}
        {title ? <h1 className={TITLE_CLASS}>{title}</h1> : null}
        <label
          htmlFor="operative-name"
          className="block text-[21px] leading-[1.35] font-semibold text-[#f4eddb]"
        >
          {prompt}
        </label>
        <p className="mt-2 text-[15px] leading-[1.45] text-[#a99d86] italic">
          {hint}
        </p>
        <input
          id="operative-name"
          autoComplete="given-name"
          enterKeyHint="done"
          readOnly={busy}
          autoCapitalize="words"
          maxLength={NAME_MAX}
          placeholder={placeholder}
          value={name}
          onChange={(e) => setName(cleanName(e.target.value))}
          className="mt-5 w-full rounded-lg border border-[var(--sm-accent-edge)] bg-black/40 px-4 py-3 text-center font-stencil text-[20px] tracking-[.12em] text-[#f4eddb] uppercase outline-none placeholder:font-sans placeholder:text-[16px] placeholder:tracking-normal placeholder:normal-case placeholder:text-[#a99d86]/60 focus:border-[var(--sm-accent)]"
        />
        <button
          type="submit"
          disabled={!valid || busy}
          className={`mt-3 w-full ${ACTION_BTN_CLASS} rounded-[9px] px-6 py-3 text-[17px] font-bold`}
        >
          {button}
        </button>
      </form>
    </section>
  );
}
